import { Shell, type QueryClient, type SessionStatus } from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import {
  ALERTE_PASTILLE,
  DemandeFonctionnaliteProvider,
  HakiliMark,
  NavRail,
  initials,
  useDemandeFonctionnalite,
  type DemandeFonctionnalite,
  type NavRailItem,
  RAIL_FOOT_ICON,
  RailAvatarMenu,
  EmptyState,
} from "@hakili/ui";
import { useState, type ReactNode } from "react";

import {
  Bell,
  Calculator,
  Calendar,
  Car,
  FolderOpen,
  Home,
  Lightbulb,
  ShieldCheck,
  User,
  UserRound,
  Users,
  Wallet,
} from "lucide-react";
import { HakiliFace } from "../routes/hakili-face.js";
import { META_TEXT } from "../routes/vehicle-row.js";
import { opensControl } from "./rights.js";
import type { DealerStore } from "./store.js";

/**
 * `P0-DLW-001`, round B of dealer onboarding: **dealer-web's shell.**
 * Rebuilt at `round F`, `F1`, into the chrome the 6 September mockup draws.
 *
 * ## What changed at `F1`, and why the old shape was right when it was written
 *
 * This shell carried a HORIZONTAL header , the dealership's name on the left,
 * two or three text links in the middle, « Se déconnecter » on the right , and
 * its own comment said why: *"the console's shell carries a rail of eleven
 * screens … a dealership's administrator had ONE screen when this file was
 * written, and a rail of one entry is furniture."* That was true of one screen
 * and stayed defensible at three.
 *
 * **The dealer has seven screens.** `docs/mockups/hakili-dealer.jsx:1044-1117`
 * draws them in a vertical `w-20` rail , mark at the top, icon above label,
 * « Une idée ? », the bell, the person , and that is the same rail the client,
 * bank and console drawings carry. `@hakili/ui`'s `NavRail` was extracted for
 * exactly this at `P1-UIK-027` and had no consumer until now; this is its
 * first, which is also the first proof that the extraction was usable.
 *
 * So the header is gone and this file's remaining job is the three things a
 * shared rail must not know: **which entries exist**, **which of them this
 * caller's rights open**, and **who is signed in**.
 *
 * ## The rail derives from `/me`, and it reads LEVELS
 *
 * `P0-DLW-018`: `/me` carries a right the caller does NOT hold, as `"non"`, so
 * a presence test offers doors the api refuses. {@link opensControl} is the
 * derivation and `rights.ts` carries the record's own table. Every entry below
 * names the right that opens it or says why it names none.
 */

const ETAT: Readonly<Record<"attente" | "connecte" | "anonyme", SessionStatus>> = {
  attente: "pending",
  connecte: "authenticated",
  anonyme: "anonymous",
};

/**
 * The api's role identifiers, and the word a person reads beside their name.
 *
 * `DECISION ADDED 05-01`: an enum value is an identifier and never a word. The
 * same two keys `routes/team.tsx` maps, and for the same reason , an unknown
 * role renders as ITSELF rather than as a default label, because a shell that
 * invented « Responsable » for a role the api had renamed would be telling a
 * person something `/me` never said.
 */
const ROLE_LABEL: Readonly<Record<string, string>> = {
  responsableConcession: "dealer.team.roleManager",
  employeConcession: "dealer.team.roleMember",
};

/**
 * `OD-18`, closed at `round F5`: **« Une idée ? », live.**
 *
 * It was `disabled` with its reason in a `title` and an `sr-only` sentence,
 * because `product_feedback` had no route a dealership could call and a modal
 * answering « Demande transmise » with nothing transmitted is the lie `R81`
 * forbids. The door exists now, so the control opens the modal the mockup wires
 * it to (`hakili-dealer.jsx:1084-1095`) and the reason sentence is deleted
 * rather than left standing beside a working button.
 *
 * It is its own component because {@link useDemandeFonctionnalite} must run
 * BENEATH the provider, and the provider wraps the whole shell.
 */
function FeatureRequestControl({ contexte }: { readonly contexte: string }): React.JSX.Element {
  const { ouvrir } = useDemandeFonctionnalite();
  return (
    <button
      type="button"
      onClick={() => {
        ouvrir(contexte);
      }}
      title={t("dealer.nav.idee")}
      aria-label={t("dealer.nav.idee")}
      className={RAIL_FOOT_ICON}
      data-testid="dealer-idee"
      data-contexte={contexte}
    >
      <Lightbulb className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

/** One rail entry, plus the right that opens it. `right: null` opens for everyone seated. */
interface EntreeRail extends NavRailItem {
  readonly right: string | null;
}

export interface DealerShellProps {
  readonly queryClient: QueryClient;
  /**
   * Passed IN rather than built here. A store constructed inside the render is
   * a new store on every render , the persona and the screen would reset on
   * each keystroke, and the bug reads as "the shell forgets things".
   */
  readonly store: DealerStore;
  /** `attente` while the session is being established, so nothing flashes. */
  readonly status: "attente" | "connecte" | "anonyme";
  /**
   * **Still taken, and no longer rendered by the chrome.**
   *
   * The founder's note of 7 September: the drawing names no dealership in the
   * rail. The prop stays because `App` passes it and the DASHBOARD names the
   * dealership , removing it here would push that read into the screen and give
   * one fact two paths out of `/me`.
   */
  readonly organisation: string | null;
  readonly nomComplet: string | null;
  /**
   * `F1`: the role `/me` answers, for the rail's own person control.
   *
   * The mockup's rail says who is signed in and not only where one is standing,
   * and its comment says why: *« avec une équipe, deux personnes ouvrent le
   * même espace et doivent se reconnaître d'un coup d'oeil »*
   * (`hakili-dealer.jsx:1105-1108`). It titles that control
   * `nom + " · " + role.label`, which is the shape below.
   */
  readonly role?: string | null;
  /**
   * `PRICE-GAP-DEALER`, M3: **how many acts wait for the dealership's hand** (a price-gap decision, a mise à
   * disposition to confirm). While it is above zero the rail wears a red dot on the entries where the acts are
   * done; it clears when the app, asking again, counts zero.
   */
  readonly aTraiter?: number;
  readonly connexion: ReactNode;
  readonly deconnecter: () => void;
  /**
   * `P0-DLW-002`: the current route's token IS its authorisation, so the gate
   * lets its holder through , 6.3.6's rule, and `AuthGate`'s own
   * `isPublicRoute`. **The chrome comes off with it**: naming a dealership to
   * somebody who has not joined it yet would tell a stranger whose invitation
   * this is, and offering « Se déconnecter » to a caller with no session is
   * furniture.
   */
  readonly publique?: boolean;
  /** The current path, and where to go. The rail reads it; it never reads `location`. */
  readonly path?: string;
  readonly naviguer?: (to: string) => void;
  /**
   * `P0-DLW-007`, corrected by `P0-DLW-018`: the rights `/me` answers, so the
   * chrome offers only doors this caller can open.
   *
   * **The matrix is untouched** , this hides a control the api refuses rather
   * than granting one it does not, and every route still answers in its own
   * words to anyone who reaches it by address.
   */
  readonly rights?: Readonly<Record<string, string>>;
  /**
   * `OD-18`: where a demand goes.
   *
   * The shell adds the SCREEN the person was standing on , it is the one part
   * of the context only the chrome knows , and the app calls the door. Left out,
   * the modal still opens and the provider's own refusal is what a person sees:
   * a control that opened onto nothing would be the dead affordance
   * `P1-UIK-025` exists to abolish.
   */
  readonly submitFeatureRequest?: (
    demande: DemandeFonctionnalite & { readonly ecran: string },
  ) => void | Promise<void>;
  /**
   * `A-DLR`, Arc B (S4-6): **the bell, live.** The rows the door answers (`GET /me/notifications`), the unread count
   * DERIVED from them, her own act of reading one, and where a row's `linkPath` leads. Absent, the bell stays
   * inert with its reason, exactly as it was; present, a dealer told that a pièce is asked of him can open it.
   */
  readonly notifications?: {
    readonly rows: readonly {
      readonly id: string;
      readonly title: string;
      readonly body: string;
      readonly linkPath: string | null;
      readonly readAt: string | null;
    }[];
    /** The badge's number as the door serves it, counted over ALL her rows: never recounted from the rows the panel holds. */
    readonly unread: number;
    readonly onRead: (id: string) => void;
    /** The one act the mark-all-read door serves; the badge becomes the number the door re-derived. */
    readonly onReadAll: () => void;
  };
  readonly children: ReactNode;
}

/**
 * R's review `44b23b76`, change 2: **the bell's badge wears the kit's one danger red** (`ERROR_TEXT`'s own value, `--hk-bad` as the bank's theme resolves it, #A82019), as a fill, with a white figure of 12 px:
 * the dealer never loads the bank's theme, so the fallback IS the value. One danger, one red; no rose-500.
 */
const BADGE_DANGER = "bg-[color:var(--hk-bad,#a82019)]";

type PanelNotices = NonNullable<DealerShellProps["notifications"]>;

/** One row of the bell's panel: the served title and body on the 14 px floor; a read title in `--hk-text-2` (R, change 1); an unread row is named « Non lue » for a screen reader. */
function NoticeRow(props: {
  readonly row: PanelNotices["rows"][number];
  readonly notifications: PanelNotices;
  readonly onClose: () => void;
  readonly naviguer: ((to: string) => void) | undefined;
}): React.JSX.Element {
  const { row, notifications, onClose, naviguer } = props;
  const unread = row.readAt === null;
  return (
    <li className="px-4 py-3">
      <button
        type="button"
        onClick={() => {
          notifications.onRead(row.id);
          onClose();
          if (row.linkPath !== null) naviguer?.(row.linkPath);
        }}
        className="block min-h-11 w-full rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
        data-lue={unread ? "non" : "oui"}
        data-testid={`dealer-notification-${row.id}`}
      >
        {unread ? <span className="sr-only">{t("dealer.nav.notificationNonLue")}</span> : null}
        <span className={"block text-sm font-medium " + (unread ? "text-gray-900" : META_TEXT)}>
          {row.title}
        </span>
        <span className="mt-0.5 block text-sm leading-relaxed text-gray-500">{row.body}</span>
      </button>
    </li>
  );
}

export function DealerShell({
  queryClient,
  store,
  status,
  nomComplet,
  role = null,
  connexion,
  deconnecter,
  publique = false,
  path = "/",
  naviguer,
  rights = {},
  aTraiter = 0,
  submitFeatureRequest,
  notifications,
  children,
}: DealerShellProps): React.JSX.Element {
  const [cloche, setCloche] = useState(false);
  const nonLues = notifications?.unread ?? 0;
  const nonLuesRows = notifications?.rows.filter((row) => row.readAt === null) ?? [];
  const luesRows = notifications?.rows.filter((row) => row.readAt !== null) ?? [];
  const fermer = (): void => {
    setCloche(false);
  };
  /**
   * **The seven entries the mockup draws**, in its own order
   * (`hakili-dealer.jsx:1046-1062`), each carrying the right that opens it.
   */
  const entries: readonly EntreeRail[] = [
    {
      screen: "accueil",
      path: "/",
      label: t("dealer.nav.accueil"),
      icon: <Home className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-accueil",
      // No right: it is the landing every seated person reaches, and a landing
      // behind a right is a session with nowhere to stand.
      right: null,
    },
    {
      screen: "estimation",
      path: "/estimation",
      label: t("dealer.nav.estimation"),
      // The rail's word is short (F2: eleven characters at most); the entry's name and the screen's name are the full words (the architect's ruling of 1 November 2026).
      accessibleName: t("dealer.nav.estimationNom"),
      icon: <Calculator className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-estimation",
      // `DEALER-PREQUAL` (round A-D1, D-1031-18): visible to every dealer user, no right. When the Comptoir screen (iteration 2) is built the entry moves there.
      right: null,
    },
    {
      screen: "vehicles",
      path: "/vehicules",
      label: t("dealer.nav.vehicules"),
      icon: <Car className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-vehicules",
      // `P0-DLW-010`: `gererInventaire`, which BOTH rungs hold , the employee
      // works the forecourt and the inventory is what that means (06 §6.1.7).
      right: "gererInventaire",
    },
    {
      screen: "prospects",
      path: "/prospects",
      label: t("dealer.nav.prospects"),
      icon: <UserRound className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-prospects",
      // `P1-DLW-054` (PROSPECTS, the architect's ruling of 2 November 2026: rail entry YES): the financing leads come from the dossiers door, so the door's own right, the inventory's, which both rungs hold. R's batch 6 draws it after « Véhicules ».
      right: "gererInventaire",
    },
    {
      screen: "dossiers",
      path: "/dossiers",
      label: t("dealer.nav.dossiers"),
      icon: <FolderOpen className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-dossiers",
      // `P1-DLW-048`: the door's own right (`gererInventaire`, as the inventory's), which both rungs hold.
      right: "gererInventaire",
    },
    {
      screen: "appointments",
      path: "/rendez-vous",
      label: t("dealer.nav.rendezVous"),
      icon: <Calendar className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-rendez-vous",
      // 06 §6.1.7: `gererVisites` , « Confirmer ou proposer un créneau, marquer
      // la visite effectuée » , `oui` for both rungs, like the inventory.
      right: "gererVisites",
    },
    {
      screen: "team",
      path: "/equipe",
      label: t("dealer.nav.equipe"),
      icon: <Users className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-equipe",
      /**
       * **`round F6`: the entry opens for BOTH rungs, on `voirEquipeConcession`.**
       *
       * `F1` bound it to `gererEquipeConcession` , `oui` for the manager,
       * **`non`** for the employee , and recorded that `OD-10` was closed by
       * the founder's words while the door had not grown the right that would
       * let him in. `round F6` grew it: the READING is its own right, `oui` for
       * both rungs, and the three gestures stay behind the old one, so the
       * employee's Équipe carries no gesture at all , which is what he ruled.
       *
       * The mockup said this would happen of itself (`:1053-1056`): *« la
       * navigation dérivant des droits que /me porte »*. Nothing was hard-coded
       * for the day it opened, and nothing is now.
       */
      right: "voirEquipeConcession",
    },
    {
      screen: "balance",
      path: "/solde",
      label: t("dealer.nav.solde"),
      icon: <Wallet className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-solde",
      // 06 §6.1.7: `voirSoldePrepaye` , « Voir le solde et ses mouvements,
      // recharger » , `oui` for the manager, **`non`** for the employee. Money
      // is the manager's, and this is the second entry the two rungs differ on.
      right: "voirSoldePrepaye",
    },
    {
      screen: "reliability",
      path: "/fiabilite",
      label: t("dealer.nav.fiabilite"),
      icon: <ShieldCheck className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-fiabilite",
      /**
       * **No right, because the record names none**, and that is reported
       * rather than papered over.
       *
       * 06 §6.1.7's dealer matrix has eleven rows and not one of them is the
       * reliability note. The screen the mockup draws (`:3679-3712`) is the
       * dealership's own standing , its grade, its financed volume, its price
       * gap against expertise , which the console already shows Hakili about
       * this same dealership. Nothing on it is another party's, and nothing on
       * it is a gesture.
       *
       * So it opens for everyone this dealership seats, on `P0-PTA-001`'s
       * reading of « Profil »: a fact about the house is readable by the
       * house. **If a door for it is ever minted with a right of its own, this
       * entry binds to that right and this comment is what is deleted.** Filed
       * as a candidate in this round's report.
       */
      right: null,
    },
    {
      screen: "profile",
      path: "/profil",
      label: t("dealer.nav.profil"),
      icon: <User className="h-5 w-5" aria-hidden="true" />,
      testId: "dealer-nav-profil",
      // `P0-PTA-001`: no right. Everyone the dealership seats may READ where it
      // is , a person on the forecourt needs the address every car they list is
      // prefilled from. The « Modifier » control inside the screen is the half
      // that is the manager's, and the door refuses anyone else regardless.
      right: null,
    },
  ];

  // The dot sits where the acts are done: Accueil lists them, Véhicules is where each is answered.
  const pastilleSur: ReadonlySet<string> = new Set(["accueil", "vehicles"]);
  const visibles = entries
    .filter((entry) => entry.right === null || opensControl(rights[entry.right]))
    .map((entry) =>
      aTraiter > 0 && pastilleSur.has(entry.screen)
        ? { ...entry, pastille: true, pastilleLabel: t("dealer.nav.aTraiter") }
        : entry,
    );

  /**
   * `D-1019-1`: « Profil » is not an entry of the RAIL (the avatar's menu opens it), but it stays an entry of this list: the
   * lit screen, the name a demand carries (`OD-18`) and the address of « Une idée ? » all read it, and on `/profil` they
   * must still say « Profil ».
   */
  const entreesDuRail = visibles.filter((entry) => entry.screen !== "profile");

  /**
   * Which entry is lit.
   *
   * **The DEEPEST matching path wins**, and the reason is `/vehicules/:id`: a
   * car's own page belongs to « Mes véhicules » and must light it, while `/`
   * must match nothing but itself or it would light « Tableau de bord » on
   * every screen in the app. That mapping is the app's , `NavRail` takes
   * `actif` as a prop precisely so a rail never becomes a second router.
   */
  const actif =
    [...visibles]
      .filter((entry) =>
        entry.path === "/"
          ? path === "/" || path === "/accueil"
          : path === entry.path || path.startsWith(`${entry.path}/`),
      )
      .sort((a, b) => b.path.length - a.path.length)[0]?.screen ?? "";

  /**
   * `OD-18`: the screen a demand came from, as a PERSON reads it.
   *
   * The rail already holds the drawn label of every entry and already knows
   * which one is lit, so the name travels from the same place the person is
   * reading it off. The door refuses an identifier-shaped label , a demand
   * whose context said `vehicleDetail` is one the product team has to decode ,
   * and this is why nothing here sends `actif`.
   */
  const litEntry = visibles.find((entry) => entry.screen === actif);
  const currentScreenLabel =
    litEntry === undefined ? t("dealer.nav.accueil") : (litEntry.accessibleName ?? litEntry.label);
  /** `"<app>:<screen>:<action>"`, the address `CONTEXTE_MOTIF` fixes. */
  const featureRequestContext = `concessionnaire:${actif === "" ? "accueil" : actif}:idee`;

  const nom = nomComplet ?? "";
  const cleRole = role === null || role === "" ? null : (ROLE_LABEL[role] ?? null);
  const titrePersonne =
    role === null || role === ""
      ? nom
      : cleRole === null
        ? // The api answered a role this build has not met. Its own word is
          // shown rather than a guess , see `ROLE_LABEL`.
          `${nom} · ${role}`
        : `${nom} · ${t(cleRole)}`;

  return (
    <DemandeFonctionnaliteProvider
      envoyer={async (demande) => {
        await submitFeatureRequest?.({ ...demande, ecran: currentScreenLabel });
      }}
    >
      <Shell
        queryClient={queryClient}
        store={store}
        // `AuthGate`'s own three words, mapped once here. The French names are
        // this app's; `pending` and not `loading` is the gate's, and guessing it
        // is how a shell renders nothing for ever.
        status={ETAT[status]}
        pending={<p className="p-6 text-sm text-gray-500">{t("common.state.chargement")}</p>}
        signIn={connexion}
        isPublicRoute={publique}
        crashMessage={t("common.error.inattendue")}
        renderError={(fallback) => (
          <p role="alert" className="p-6 text-sm text-gray-500">
            {fallback.message}
          </p>
        )}
        layout={(outlet) =>
          publique ? (
            // No rail, and therefore no organisation name and no way to leave:
            // there is no session to leave, and the name belongs to a dealership
            // the visitor has not joined.
            <div className="min-h-screen bg-gray-50">
              <main className="mx-auto w-full max-w-xl p-6">{outlet}</main>
            </div>
          ) : (
            // The mockup's own root: `h-screen flex`, the rail, then the screen
            // scrolling on its own (`hakili-dealer.jsx:4168-4170`). Each screen
            // carries its own title and its own width, which is why there is no
            // `max-w` here any more.
            <div className="flex h-screen bg-gray-50 text-gray-900 antialiased">
              <NavRail
                items={entreesDuRail}
                actif={actif}
                mark={<HakiliMark />}
                accueilPath="/"
                naviguer={(to) => {
                  naviguer?.(to);
                }}
                footer={
                  <>
                    {/**
                     * **« Une idée ? » is rendered INERT, and says why.**
                     *
                     * The mockup wires it to `DemandeFonctionnaliteModal`
                     * (`:1084-1095`), and `@hakili/ui` carries that modal already
                     * , `P1-UIK-025`, whose own rule is that a stub must be LIVE
                     * because *"a greyed-out button records nothing"*.
                     *
                     * **It is disabled here anyway, and the conflict is reported
                     * rather than resolved in passing.** `P1-UIK-025`'s provider
                     * takes `envoyer` as an OPTIONAL prop, and this app has
                     * nothing to give it: `product_feedback` has no route a
                     * dealership can call, and `analyserDemandesFonctionnalites`
                     * is Hakili's right, not a dealer's. A modal that answered
                     * « Demande transmise » with nothing transmitted would be the
                     * exact lie `R81` forbids, and worse than a control that says
                     * plainly what it is waiting for. The day the door lands this
                     * becomes an `AVenir` with a real `envoyer`, and the
                     * furniture does not move.
                     */}
                    <FeatureRequestControl contexte={featureRequestContext} />

                    {/**
                     * **The bell, present and without a dot.**
                     *
                     * The mockup draws a `bg-rose-500` pastille on it
                     * (`hakili-dealer.jsx:1101-1104`). No notification fact
                     * exists in this product , `notifications` carries no route
                     * this surface can call , so a dot here would stand for a
                     * count nobody computed, which is `R81` exactly. The token is
                     * named in `@hakili/ui` as {@link ALERTE_PASTILLE} and worn
                     * by nothing, so the day a fact arrives the change is one
                     * line and the colour is not chosen again from memory.
                     */}
                    {notifications === undefined ? (
                      <button
                        type="button"
                        disabled
                        title={t("dealer.nav.notificationsRaison")}
                        aria-label={t("dealer.nav.notifications")}
                        className="relative flex h-7 w-7 cursor-not-allowed items-center justify-center text-gray-300"
                        data-testid="dealer-cloche"
                        data-pastille={ALERTE_PASTILLE}
                      >
                        <Bell className="h-5 w-5" aria-hidden="true" />
                      </button>
                    ) : (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            setCloche((ouverte) => !ouverte);
                          }}
                          title={t("dealer.nav.notifications")}
                          aria-label={t("dealer.nav.notifications")}
                          className={RAIL_FOOT_ICON}
                          data-testid="dealer-cloche"
                          data-pastille={ALERTE_PASTILLE}
                        >
                          <Bell className="h-5 w-5" aria-hidden="true" />
                          {nonLues === 0 ? null : (
                            <span
                              className={`absolute -right-1 top-0 rounded-full px-1.5 text-xs font-semibold text-white ${BADGE_DANGER}`}
                              data-testid="dealer-cloche-compte"
                            >
                              {nonLues}
                            </span>
                          )}
                        </button>
                        {cloche ? (
                          <div
                            className="fixed bottom-4 left-24 z-40 w-80 rounded-2xl border border-gray-200 bg-white text-left shadow-xl"
                            data-testid="dealer-notifications-panneau"
                          >
                            <div
                              className="flex items-center justify-between gap-2 border-b border-gray-100 py-1 pl-4 pr-2"
                              data-testid="dealer-notifications-entete"
                            >
                              <p className="text-sm font-semibold text-gray-900">
                                {t("dealer.nav.notifications")}
                              </p>
                              <button
                                type="button"
                                onClick={() => {
                                  setCloche(false);
                                }}
                                className={`flex min-h-11 min-w-11 items-center justify-center rounded-lg px-2 text-sm font-medium ${META_TEXT} hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600`}
                                data-testid="dealer-notifications-fermer"
                              >
                                {t("dealer.nav.notificationsFermer")}
                              </button>
                            </div>
                            {notifications.rows.length === 0 ? (
                              <div data-testid="dealer-notifications-vide">
                                {/* The kit's `mascot` slot holds lane F's HakiliFace (#915), resting, as it is: never forked, never redrawn. The kit's description is 12 px; the panel's sentence stands on the 14 px floor (R, DX-015). */}
                                <EmptyState
                                  mascot={<HakiliFace />}
                                  title={t("dealer.nav.notificationsVideTitre")}
                                  description={
                                    <span
                                      className="block text-sm leading-5"
                                      data-testid="dealer-notifications-vide-texte"
                                    >
                                      {t("dealer.nav.notificationsVideTexte")}
                                    </span>
                                  }
                                />
                              </div>
                            ) : (
                              <div className="max-h-96 overflow-y-auto">
                                {nonLues === 0 ? null : (
                                  <div className="px-2 pt-1">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        notifications.onReadAll();
                                      }}
                                      className="flex min-h-11 items-center rounded-lg px-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                                      data-testid="dealer-notifications-tout-lu"
                                    >
                                      {t("dealer.nav.notificationsToutLu")}
                                    </button>
                                  </div>
                                )}
                                {nonLuesRows.length === 0 ? null : (
                                  <>
                                    <h3
                                      className="px-4 pt-2 text-sm font-semibold text-gray-900"
                                      data-testid="dealer-notifications-groupe-nouveau"
                                    >
                                      {t("dealer.nav.notificationsNouveau").replace("{n}", String(nonLues))}
                                    </h3>
                                    <ul className="divide-y divide-gray-100">
                                      {nonLuesRows.map((row) => (
                                        <NoticeRow
                                          key={row.id}
                                          row={row}
                                          notifications={notifications}
                                          onClose={fermer}
                                          naviguer={naviguer}
                                        />
                                      ))}
                                    </ul>
                                  </>
                                )}
                                {luesRows.length === 0 ? null : (
                                  <>
                                    <h3
                                      className={`px-4 pt-3 text-sm font-semibold ${META_TEXT}`}
                                      data-testid="dealer-notifications-groupe-plus-tot"
                                    >
                                      {t("dealer.nav.notificationsPlusTot")}
                                    </h3>
                                    <ul className="divide-y divide-gray-100">
                                      {luesRows.map((row) => (
                                        <NoticeRow
                                          key={row.id}
                                          row={row}
                                          notifications={notifications}
                                          onClose={fermer}
                                          naviguer={naviguer}
                                        />
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}

                    {/* The person, from `/me`: initials seen, « nom · rôle »
                    read. `initials()` is `P0-UIK-001`'s, so "Awa Traoré" gives
                    AT here and everywhere else in the product.

                    `D-1019-1`: the avatar is a MENU , « Profil », which left the
                    list of entries, and « Se déconnecter » (`00-SPINE` 6.2: every
                    surface can be left, and leaving is an action in the chrome,
                    not a setting inside a profile screen; the mockup draws no way
                    out, so this is the record's requirement placed where the rail
                    can hold it). */}
                    <RailAvatarMenu
                      initials={initials(nom)}
                      name={titrePersonne}
                      tone="bg-indigo-100 text-indigo-700"
                      testId="dealer-personne"
                      wide
                      items={[
                        {
                          label: t("dealer.nav.profil"),
                          testId: "dealer-nav-profil",
                          onSelect: () => {
                            naviguer?.("/profil");
                          },
                        },
                        {
                          label: t("dealer.nav.deconnexion"),
                          testId: "dealer-deconnexion",
                          onSelect: deconnecter,
                        },
                      ]}
                    />
                  </>
                }
              />
              {/**
               * **The screen owns its own frame**, as the mockup does.
               *
               * `F1` kept a `mx-auto max-w-4xl px-8 py-8` here, borrowed from the
               * old header shell, so `I-A`'s screens would not go full-bleed the
               * moment the chrome changed , a container removed and its
               * replacements written in the same breath means no screen in the
               * app is ever correct in between.
               *
               * `F3` is what it was waiting for. The drawing gives every screen
               * its own `<main>` with its own width and its own scrolling ,
               * `max-w-5xl` on the dashboard, `max-w-4xl` on the reliability
               * note, and **no width at all on « Mes véhicules »**, which is a
               * two-column split scrolling in each column separately
               * (`hakili-dealer.jsx:1652-1673`). One container here could not be
               * both.
               */}
              {outlet}
            </div>
          )
        }
      >
        {children}
      </Shell>
    </DemandeFonctionnaliteProvider>
  );
}
